'use client';

import { useEffect, useId, useState } from 'react';
import {
  ArrowUpRight,
  Check,
  Clock3,
  Gift,
  Languages,
} from 'lucide-react';
import { EquipeGlassTooltipContent } from '@/components/help-center/EquipeGlassTooltipContent';
import { cn } from '@/components/help-center/latest-power-shared';
import {
  avatarFieldDirectImageUrl,
  xatWebGearAvatarUrl,
} from '@/components/help-center/equipe-shared';
import { Tooltip, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  BOT_PROVIDERS,
  BOT_PROVIDERS_SECTION,
  type BotProvider,
  type BotProviderStatId,
} from '@/lib/bot-providers';
import { getSmilieImageUrl, getXatProfile } from '@/services/xat-proxy.service';

const BOT_PROVIDER_PAWN_SRC = '/pawns/sky.png';
const BOT_PROVIDER_PAWN_LABEL = 'Provedor de Bot';
const BOT_SECTION_ICON_SRC = '/icons/botevery.webp';

const STAT_ICONS: Record<BotProviderStatId, typeof Gift> = {
  availability: Gift,
  trial: Clock3,
  multilingual: Languages,
};

/** Resolve o campo `avatar` do xat (URL, toon numérico ou smilie). */
function resolveXatAvatarSrc(avatarField: string | null | undefined): string | null {
  const raw = String(avatarField ?? '').trim();
  if (!raw) return null;

  const direct = avatarFieldDirectImageUrl(raw);
  if (direct) return direct;

  const gear = xatWebGearAvatarUrl(raw);
  if (gear) return gear;

  const smilie = raw.match(/^\(?([a-zA-Z][a-zA-Z0-9]*)\)?$/);
  if (smilie) return getSmilieImageUrl(smilie[1], 64);

  return null;
}

/**
 * Avatares Animate no xat costumam ser GIF → strip PNG (frames em fila).
 * Preferimos a URL .gif quando existir; senão reproduzimos o strip como animação.
 */
function preferGifUrl(url: string): string {
  return url.replace(/\.png(\?.*)?$/i, '.gif$1');
}

type StripMeta = { frames: number; frameSize: number };

function detectStrip(img: HTMLImageElement): StripMeta | null {
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  if (!h || w < h * 2) return null;
  if (w % h !== 0) return null;
  return { frames: w / h, frameSize: h };
}

function ProviderLogo({
  name,
  logoUrl,
  providerId,
}: {
  name: string;
  logoUrl: string | null;
  providerId: string;
}) {
  const initials = name
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const isEvox = providerId === 'evoxbot';

  if (logoUrl) {
    return (
      <div className="relative h-[4.5rem] w-[4.5rem] shrink-0 overflow-hidden rounded-full ring-2 ring-sky-400/35 ring-offset-2 ring-offset-[#02001a] sm:h-20 sm:w-20">
        <img
          src={logoUrl}
          alt=""
          className={cn(
            'h-full w-full',
            // EVOX: ícone pequeno no PNG — amplia só o desenho, mantém o círculo no tamanho padrão
            isEvox ? 'scale-[1.85] object-contain' : 'object-cover'
          )}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex h-[4.5rem] w-[4.5rem] shrink-0 items-center justify-center rounded-full font-poppins text-xl font-bold text-sky-100 sm:h-20 sm:w-20',
        'bg-gradient-to-br from-[#0a1a5c] via-[#050530] to-[#02001a]',
        'ring-2 ring-sky-400/35 ring-offset-2 ring-offset-[#02001a]',
        'shadow-[0_0_28px_-8px_rgba(56,189,248,0.4)]'
      )}
      aria-hidden
    >
      {initials}
    </div>
  );
}

function OwnerAvatarMedia({ src }: { src: string }) {
  const [displaySrc, setDisplaySrc] = useState(src);
  const [strip, setStrip] = useState<StripMeta | null>(null);

  useEffect(() => {
    let cancelled = false;
    setStrip(null);
    setDisplaySrc(src);

    const loadImage = (url: string) =>
      new Promise<{ url: string; img: HTMLImageElement } | null>((resolve) => {
        const img = new Image();
        img.onload = () => {
          if (img.naturalWidth < 2 || img.naturalHeight < 2) {
            resolve(null);
            return;
          }
          resolve({ url, img });
        };
        img.onerror = () => resolve(null);
        img.src = url;
      });

    const run = async () => {
      const gifCandidate = /\.png(\?|$)/i.test(src) ? preferGifUrl(src) : null;
      if (gifCandidate && gifCandidate !== src) {
        const gif = await loadImage(gifCandidate);
        if (!cancelled && gif && !detectStrip(gif.img)) {
          setDisplaySrc(gif.url);
          setStrip(null);
          return;
        }
      }

      const png = await loadImage(src);
      if (cancelled || !png) return;
      setDisplaySrc(png.url);
      setStrip(detectStrip(png.img));
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [src]);

  if (strip) {
    const displayPx = 36; // h-9 w-9
    const durationSec = Math.min(12, Math.max(0.9, strip.frames * 0.09));
    return (
      <div
        className="h-full w-full"
        style={{
          backgroundImage: `url(${JSON.stringify(displaySrc)})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: `${displayPx * strip.frames}px ${displayPx}px`,
          animation: `bot-owner-strip ${durationSec}s steps(${strip.frames}) infinite`,
          ['--bot-strip-end' as string]: `${-displayPx * strip.frames}px`,
        }}
        role="img"
        aria-hidden
      />
    );
  }

  return <img src={displaySrc} alt="" className="h-full w-full object-cover" />;
}


function OwnerAvatar({ name }: { name: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const initials = name.slice(0, 2).toUpperCase();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const profile = await getXatProfile({ regname: name });
        if (cancelled) return;
        setSrc(resolveXatAvatarSrc(profile.avatarUrl));
      } catch {
        if (!cancelled) setSrc(null);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [name]);

  if (src) {
    return (
      <div className="relative h-9 w-9 shrink-0 overflow-hidden rounded-full ring-2 ring-sky-400/30 ring-offset-2 ring-offset-[#02001a]">
        <OwnerAvatarMedia src={src} />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-poppins text-xs font-bold text-sky-100',
        'bg-gradient-to-br from-[#0a1a5c] via-[#050530] to-[#02001a]',
        'ring-2 ring-sky-400/30 ring-offset-2 ring-offset-[#02001a]'
      )}
      aria-hidden
    >
      {initials}
    </div>
  );
}

function ProviderHeader({ provider }: { provider: BotProvider }) {
  return (
    <header
      className={cn(
        'relative overflow-hidden rounded-2xl border border-blue-950/80',
        'bg-[#02001a] shadow-lg shadow-black/30 ring-1 ring-white/[0.06]',
        'transition-shadow duration-300 hover:shadow-[0_0_40px_-16px_rgba(37,99,235,0.35)]'
      )}
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        <div className="absolute inset-0 bg-[#02001a]" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#010833] via-[#030023] to-[#010010]" />
        {/* Azul claro só na ponta (canto superior direito) */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(125,211,252,0.28)_0%,rgba(56,189,248,0.1)_18%,transparent_42%)]" />
      </div>
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-300/35 to-transparent"
        aria-hidden
      />

      <div className="relative z-10 flex flex-col gap-5 p-5 sm:gap-6 sm:p-6 lg:p-7">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
          <div className="flex min-w-0 flex-1 gap-4 sm:gap-5">
            <ProviderLogo
              name={provider.name}
              logoUrl={provider.logoUrl}
              providerId={provider.id}
            />
            <div className="min-w-0 flex-1">
              <h3 className="font-poppins text-2xl font-bold tracking-tight text-white sm:text-[1.75rem]">
                {provider.name}
              </h3>
              <p className="mt-1.5 max-w-xl font-inter text-sm leading-relaxed text-blue-100/70">
                {provider.tagline}
              </p>
            </div>
          </div>

          <div className="flex shrink-0 flex-col gap-3 sm:items-end">
            <a
              href={provider.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5',
                'bg-sky-300 font-poppins text-sm font-semibold text-[#02001a]',
                'shadow-[0_0_20px_-6px_rgba(125,211,252,0.55)] transition-colors',
                'hover:bg-sky-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-200/60'
              )}
            >
              {provider.websiteLabel}
              <ArrowUpRight className="h-4 w-4" aria-hidden />
            </a>
          </div>
        </div>

        <div className="flex flex-col gap-4 border-t border-white/[0.07] pt-4 sm:flex-row sm:items-center sm:justify-between">
          {provider.owner ? (
            <div className="flex items-center gap-3">
              <OwnerAvatar name={provider.owner.name} />
              <div className="min-w-0">
                <p className="font-poppins text-[10px] font-semibold uppercase tracking-wide text-blue-300/60">
                  Responsável
                </p>
                <div className="flex min-w-0 items-center">
                  <TooltipProvider delayDuration={250}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          className="-ml-1 inline-flex shrink-0 cursor-default items-center justify-center rounded-md outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#02001a]"
                          aria-label={BOT_PROVIDER_PAWN_LABEL}
                        >
                          <img
                            src={BOT_PROVIDER_PAWN_SRC}
                            alt=""
                            className="pointer-events-none block h-5 w-5 object-contain object-center"
                            width={20}
                            height={20}
                            draggable={false}
                            loading="lazy"
                          />
                        </span>
                      </TooltipTrigger>
                      <EquipeGlassTooltipContent>
                        <p className="font-poppins text-[12px] font-semibold tracking-wide text-white">
                          {BOT_PROVIDER_PAWN_LABEL}
                        </p>
                      </EquipeGlassTooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <a
                    href={provider.owner.xatspaceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="-ml-1.5 min-w-0 truncate font-poppins text-sm font-semibold text-white transition-colors hover:text-sky-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40"
                  >
                    {provider.owner.name}
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <div />
          )}

          <nav aria-label={`Menu ${provider.name}`} className="overflow-x-auto">
            <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
              {provider.nav.map((item) => (
                <li key={item.id}>
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      'inline-flex items-center whitespace-nowrap rounded-lg border border-sky-300/20',
                      'bg-sky-300/10 px-3 py-1.5 font-poppins text-[11px] font-semibold text-sky-100',
                      'shadow-sm shadow-black/10 transition-colors',
                      'hover:border-sky-300/35 hover:bg-sky-300/20 hover:text-white',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40'
                    )}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    </header>
  );
}

function ProviderStats({ provider }: { provider: BotProvider }) {
  return (
    <section
      id={`bot-provider-${provider.id}-resources`}
      className="scroll-mt-24"
      aria-labelledby={`bot-provider-${provider.id}-stats-heading`}
    >
      <h4
        id={`bot-provider-${provider.id}-stats-heading`}
        className="mb-3 font-poppins text-xs font-semibold uppercase tracking-wide text-blue-600"
      >
        Informações do serviço
      </h4>
      <ul className="m-0 grid list-none grid-cols-1 gap-2.5 p-0 sm:grid-cols-3">
        {provider.stats.map((stat) => {
          const Icon = STAT_ICONS[stat.id];
          return (
            <li
              key={stat.id}
              className={cn(
                'rounded-xl border border-gray-200/90 bg-white p-3.5 shadow-sm shadow-blue-950/[0.03]',
                'transition-colors hover:border-sky-300/60'
              )}
            >
              <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-lg bg-sky-50 text-sky-600">
                <Icon className="h-4 w-4" aria-hidden />
              </div>
              <p className="font-poppins text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                {stat.label}
              </p>
              <p className="mt-0.5 font-poppins text-sm font-bold text-gray-900">{stat.value}</p>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function ProviderBenefits({ provider }: { provider: BotProvider }) {
  return (
    <section
      id={`bot-provider-${provider.id}-benefits`}
      className="scroll-mt-24"
      aria-labelledby={`bot-provider-${provider.id}-benefits-heading`}
    >
      <h4
        id={`bot-provider-${provider.id}-benefits-heading`}
        className="mb-3 font-poppins text-xs font-semibold uppercase tracking-wide text-blue-600"
      >
        Por que escolher este provedor?
      </h4>
      <ul className="m-0 grid list-none gap-2.5 p-0">
        {provider.benefits.map((benefit) => (
          <li
            key={benefit.id}
            className="flex gap-3 rounded-xl border border-gray-200/90 bg-gradient-to-br from-white to-sky-50/40 p-4"
          >
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600">
              <Check className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
            </span>
            <div className="min-w-0">
              <p className="font-poppins text-sm font-semibold text-gray-900">{benefit.title}</p>
              <p className="mt-1 font-inter text-[13px] leading-relaxed text-gray-600">
                {benefit.description}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ProviderCard({ provider }: { provider: BotProvider }) {
  return (
    <article
      id={`bot-provider-${provider.id}-overview`}
      className={cn(
        'scroll-mt-24 overflow-hidden rounded-[1.35rem] border border-gray-200/90 bg-white',
        'shadow-[0_18px_40px_-28px_rgba(15,23,42,0.35)]'
      )}
    >
      <ProviderHeader provider={provider} />
      <div className="space-y-8 p-5 sm:p-6 lg:p-7">
        <ProviderStats provider={provider} />
        <ProviderBenefits provider={provider} />
      </div>
    </article>
  );
}

/** Secção especial — só na página do power Bot. */
export default function PowerBotProvidersSection() {
  const headingId = useId();

  return (
    <section
      className="relative border-t border-gray-100 bg-gradient-to-b from-gray-50 via-white to-sky-50/40"
      aria-labelledby={headingId}
    >
      <div className="mx-auto w-full max-w-[92rem] px-5 py-14 sm:px-8 sm:py-16 lg:px-10 lg:py-20">
        <header className="mb-10 max-w-4xl sm:mb-12 lg:max-w-5xl">
          <div className="flex items-center gap-0">
            <img
              src={BOT_SECTION_ICON_SRC}
              alt=""
              className="-ml-1 h-16 w-16 shrink-0 object-contain sm:h-20 sm:w-20 lg:h-24 lg:w-24"
              width={96}
              height={96}
              draggable={false}
            />
            <div className="min-w-0 -translate-x-3 sm:-translate-x-4">
              <span className="font-poppins text-xs font-semibold uppercase tracking-wide text-blue-600">
                {BOT_PROVIDERS_SECTION.eyebrow}
              </span>
              <h2
                id={headingId}
                className="mt-1 font-poppins text-[clamp(1.5rem,3vw,2rem)] font-bold leading-tight tracking-tight text-gray-900"
              >
                {BOT_PROVIDERS_SECTION.title}
              </h2>
            </div>
          </div>
          <p className="mt-4 max-w-none font-inter text-[15px] leading-relaxed text-gray-600">
            {BOT_PROVIDERS_SECTION.description}
          </p>
          <p className="mt-4 max-w-none rounded-xl border border-amber-200/80 bg-amber-50/80 px-4 py-3 font-inter text-sm leading-relaxed text-amber-950/80">
            {BOT_PROVIDERS_SECTION.disclaimer}
          </p>
        </header>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-7 lg:gap-8">
          {BOT_PROVIDERS.map((provider) => (
            <ProviderCard key={provider.id} provider={provider} />
          ))}
        </div>
      </div>
    </section>
  );
}
